import { atom, createStore, Provider, useAtom, type WritableAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { createContext, useContext, useMemo } from "react";

import type { UploadResults } from "@blis/shared/models/upload";

const initialValues = {
  receive: false,
  unreceive: false,
  hold: false,
  clone: false,
  instrument: false,
  editDonor: false,
  moveToSubject: false,
  editClient: false,
  editTests: false,
  editSamples: false,
  editBilling: false,
  editAdditional: false,
  addSample: false,
  addTests: false,
  cancel: false,
  restore: false,
  delete: false,
  upload: null as { files: File[]; type?: "report" | "results" } | null,
  results: null as UploadResults["result"] | null,
  samplesTable: false,
  clearAll: false,
  enterResults: null as { orderId: number; resultId?: number } | null,
  cancelTestOrder: null as {
    order: {
      id: number;
      isReflex?: boolean;
      dependedById?: number | null;
      results: { id: number; isReflex: boolean }[];
    };
  } | null,
  cancelTestOrderResult: null as { orderId: number; resultId: number } | null,
  clearTestResult: null as { resultId: number } | null,
  updateTestResult: null as { resultId: number } | null,
  cancelTestResult: null as { resultId: number } | null,
  restoreTestOrder: null as { orderId: number } | null,
  restoreTestResult: null as { resultId: number } | null,
  deleteTestOrder: null as { orderId: number } | null,
  cancelPanel: null as { instructionId: number } | null,
  restorePanel: null as { instructionId: number } | null,
  deletePanel: null as { instructionId: number } | null,
  assignSample: null as {
    orderId: number;
    currentSampleIds: number[] | null | undefined;
  } | null,
};
type DialogValues = typeof initialValues;
export type DialogName = keyof DialogValues;

const dialogStateAtom = atom(initialValues);

function createAtomFamily<T extends DialogName>() {
  return atomFamily((key: T) =>
    atom(
      (get) => {
        return get(dialogStateAtom)[key];
      },
      (_get, set, value: DialogValues[T]) => {
        set(dialogStateAtom, (prev) => {
          return {
            ...prev,
            [key]: value,
          };
        });
      },
    ),
  );
}

const dialogStateFamily = createAtomFamily();

// export function useDialogState<K extends DialogName>(key: K): [DialogValues[K], (value: DialogValues[K]) => void] {
export function useDialogState<K extends DialogName>(key: K) {
  type Val = DialogValues[K];
  const atom = dialogStateFamily(key) as unknown as WritableAtom<
    Val,
    Val[],
    Val
  >;
  return useAtom(atom);
}

export function useDialogProps<K extends DialogName>(key: K) {
  type Val = DialogValues[K];
  const atom = dialogStateFamily(key) as unknown as WritableAtom<
    Val,
    Val[],
    Val
  >;
  const [open, onOpenChange] = useAtom(atom);
  return { open, onOpenChange };
}

export function useSetDialogState<K extends DialogName>(): (
  key: K,
  value: DialogValues[K],
) => void;
export function useSetDialogState<K extends DialogName>(
  key: K,
): (value: DialogValues[K]) => void;
export function useSetDialogState<K extends DialogName>(key?: K) {
  const { store } = useContext(AccessionDialogContext);
  return key
    ? (value: DialogValues[K]) => {
        store.set(dialogStateFamily(key), value);
      }
    : (key: DialogName, value: boolean) => {
        store.set(dialogStateFamily(key), value);
      };
}

type AccessionDialogContextType = {
  store: ReturnType<typeof createStore>;
};

export const AccessionDialogContext = createContext(
  {} as AccessionDialogContextType,
);

export function AccessionDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = useMemo(() => createStore(), []);
  return (
    <Provider store={store}>
      <AccessionDialogContext.Provider value={{ store }}>
        {children}
      </AccessionDialogContext.Provider>
    </Provider>
  );
}
